import Link from 'next/link';
import { FC } from 'react';
import { IoChevronForward } from 'react-icons/io5';

import { Box, Stack, Text } from '@mantine/core';

import { CompanyProfile } from 'models';
import { CollateralGroup, CollateralHelpers } from 'helpers/CollateralHelpers';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { IntlHelpers } from 'helpers/IntlHelpers';

import { CollateralCover } from 'components/collateral/CollateralCover';
import { useCollateralItems } from 'components/company-profile/hooks/use-collateral-items';
import { Section } from 'components/section/Section';

import cx from './Collateral.module.scss';

const Collateral: FC<{ company: CompanyProfile }> = ({ company }) => {
    const { items } = useCollateralItems(company);

    if (!items.length) {
        return null;
    }

    const single = items.length === 1 ? items[0] : null;
    const firstVideo = items.find(({ collateral }) => CollateralHelpers.isPlayable(collateral));
    const covers = [...items.filter((item) => item !== firstVideo).slice(0, firstVideo ? 2 : 3), firstVideo].filter(
        (item) => !!item,
    );

    const presentGroups = (
        [
            { group: CollateralGroup.DOCUMENTS, singular: 'document', plural: 'documents', noun: 'brochures' },
            { group: CollateralGroup.VIDEOS, singular: 'video', plural: 'videos', noun: 'videos' },
            { group: CollateralGroup.IMAGES, singular: 'image', plural: 'images', noun: 'photos' },
        ] as const
    )
        .map((entry) => ({
            ...entry,
            count: items.filter(({ collateral }) => CollateralHelpers.group(collateral) === entry.group).length,
        }))
        .filter(({ count }) => count > 0);

    // Non-breaking spaces keep each count on one line when the breakdown wraps
    const breakdown = presentGroups.map(({ count, singular, plural }) =>
        IntlHelpers.formatCount(count, singular, plural).replace(' ', '\u00a0'),
    );
    const nouns = presentGroups.map(({ noun }) => noun);
    const title = `Explore our ${nouns.length > 1 ? `${nouns.slice(0, -1).join(', ')} and ${nouns.at(-1)}` : nouns[0]}`;

    return (
        <Section title="Collateral" id="collateral">
            <Link href={single ? single.href : CompanyHelpers.urls.collateral(company.slug)} className={cx.banner}>
                <Box className={cx.stack} data-count={covers.length}>
                    {covers.map((item) => (
                        <Box key={item.key} className={cx.cover}>
                            <CollateralCover collateral={item.collateral} width={120} alt={item.title} iconSize={20} />
                        </Box>
                    ))}
                </Box>

                <Stack gap={4} className={cx.content}>
                    <Text className={cx.title} lineClamp={2}>
                        {single ? single.title : title}
                    </Text>
                    <Text className={cx.meta}>
                        {single ? CollateralHelpers.caption(single.collateral) : breakdown.join(' · ')}
                    </Text>
                </Stack>

                <Text className={cx.cta}>
                    {single ? 'Open' : 'Browse collateral'}
                    <IoChevronForward />
                </Text>
            </Link>
        </Section>
    );
};

export { Collateral };
